import { computed, defineComponent, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import validator from 'validator';
import { Button, Input } from '@yellowcard/components-library-vue3';
import TransactHeader from '@/components/transact/TransactHeader.vue';
import LoaderComponent from '@/components/general/LoaderComponent.vue';
import InlineMessageAlert from '@/components/general/InlineMessageAlert.vue';
import { getChannelLabel, isValidWalletAddress, nameIsInvalid, numberFormat, } from '@/utils/helpers';
import { MIXPANEL_EVENTS, trackEvent } from '@/utils/mixpanel';
import router from '@/router';
import { useAuthStore } from '@/store/modules/auth';
import { useInterceptorStore } from '@/store/modules/interceptor';
import { useKycStore } from '@/store/modules/kyc';
import { usePaymentStore } from '@/store/modules/payment';
import useTransactionsStore from '@/store/modules/transactions';
import { KycTypes } from '@/types/kyc';
import { PaymentTypes } from '@/types/payment';
import { TransactTypes } from '@/types/transact';
import { WidgetApiServiceTypes } from '@/types/widgetApiService';
export default defineComponent({
    name: 'WalletAddress',
    components: {
        LoaderComponent,
        Button,
        Input,
        TransactHeader,
        InlineMessageAlert,
    },
    setup() {
        const transactionsStore = useTransactionsStore();
        const { selectedCrypto, selectedCryptoAmount, walletAddress, currentStep, loadingMessage, selectedChannel, walletTagRequired, travelRuleData, selectedCurrency, usdRate, selectedCurrencyAmount, } = storeToRefs(transactionsStore);
        const paymentStore = usePaymentStore();
        const { currentStep: paymentCurrentStep } = storeToRefs(paymentStore);
        const authStore = useAuthStore();
        const { isLoggedIn, partnerId, user } = storeToRefs(authStore);
        const kycStore = useKycStore();
        const { kycStatus } = storeToRefs(kycStore);
        const interceptorStore = useInterceptorStore();
        const { loading: interceptorLoading, error: interceptorError } = storeToRefs(interceptorStore);
        const error = ref(undefined);
        const buttonDisabled = ref(true);
        const { ChannelTypes } = WidgetApiServiceTypes;
        const tagText = `${selectedCrypto.value.networkType} tag/memo`;
        const rules = computed(() => ({
            firstNames: (value) => {
                if (validator.isEmpty(value) || nameIsInvalid(value))
                    return ' ';
                return true;
            },
            lastName: (value) => {
                if (validator.isEmpty(value) || nameIsInvalid(value))
                    return ' ';
                return true;
            },
            purposeOfTransfer: (value) => {
                if (validator.isEmpty(value) || nameIsInvalid(value))
                    return ' ';
                return true;
            },
        }));
        const isCountryBotswana = () => {
            if (!user.value?.country) {
                return selectedCurrency.value.currency === 'BWP';
            }
            return user.value?.country === 'Botswana';
        };
        const shouldTriggerTravelRule = computed(() => {
            const buyLocal = selectedCurrencyAmount.value.amount;
            const buyUSD = buyLocal / usdRate.value;
            const userCountry = user.value?.country;
            const buyCurrency = selectedCurrency.value.currency;
            if (buyUSD > 3000) {
                return true;
            }
            if (buyUSD > 1000 &&
                (userCountry === 'Nigeria' || buyCurrency === 'NGN')) {
                return true;
            }
            if (!userCountry) {
                if (buyCurrency === 'ZAR') {
                    return true;
                }
            }
            else if (userCountry === 'South Africa') {
                return true;
            }
            return false;
        });
        const checkIfButtonIsDisabled = (inputWalletAddress, tag) => {
            if (shouldTriggerTravelRule.value) {
                const firstNameValidation = rules.value.firstNames(travelRuleData.value.firstName);
                const lastNameValidation = rules.value.lastName(travelRuleData.value.lastName);
                const isFirstNameInvalid = firstNameValidation !== true;
                const isLastNameInvalid = lastNameValidation !== true;
                if (isFirstNameInvalid || isLastNameInvalid) {
                    return true;
                }
            }
            if (walletTagRequired.value) {
                return !(!validator.isEmpty(inputWalletAddress) && !validator.isEmpty(tag));
            }
            return validator.isEmpty(inputWalletAddress);
        };
        if (walletAddress.value.address) {
            buttonDisabled.value = checkIfButtonIsDisabled(walletAddress.value.address, walletAddress.value.tag);
        }
        const loading = ref(false);
        const showHome = computed(() => {
            if (isLoggedIn.value) {
                return kycStatus.value === KycTypes.KycStatus.APPROVED;
            }
            return false;
        });
        const network = computed(() => {
            const { standard: standard_, token: token_, networkType: networkType_, } = selectedCrypto.value;
            if (networkType_)
                return networkType_.toLocaleUpperCase();
            const tokens = TransactTypes.TokenNetworkMapping[token_] || [];
            if (standard_) {
                return (tokens.find(_token => _token.includes(standard_.toLocaleUpperCase())) || standard_);
            }
            return tokens[0];
        });
        watch(network, () => {
            walletTagRequired.value = network.value === TransactTypes.Network.TON;
        });
        const onInputWalletEntered = (value) => {
            buttonDisabled.value = true;
            buttonDisabled.value = checkIfButtonIsDisabled(value, walletAddress.value.tag);
        };
        const onInputTagEntered = (value) => {
            buttonDisabled.value = checkIfButtonIsDisabled(walletAddress.value.address, value);
        };
        const onNameInputEntered = () => {
            buttonDisabled.value = checkIfButtonIsDisabled(walletAddress.value.address, walletAddress.value.tag);
        };
        const getBankDetails = async () => {
            error.value = '';
            interceptorError.value = '';
            const fullAddress = walletTagRequired.value
                ? `${walletAddress.value.address}_${walletAddress.value.tag}`
                : walletAddress.value.address;
            const addressValid = isValidWalletAddress({
                address: fullAddress,
                token: selectedCrypto.value.token,
                standard: selectedCrypto.value.standard,
                networkType: selectedCrypto.value.networkType,
            });
            if (!addressValid) {
                error.value = 'Invalid wallet address and network pair';
                return;
            }
            if (selectedChannel.value === ChannelTypes.MOMO) {
                paymentCurrentStep.value = PaymentTypes.PaymentSteps.PAYMENT_3;
                return;
            }
            trackEvent(MIXPANEL_EVENTS.BUY.WALLET_ADDRESS, {
                payment_method: getChannelLabel(selectedChannel.value),
                partner: partnerId.value,
            });
            error.value = undefined;
            buttonDisabled.value = true;
            loading.value = true;
            loadingMessage.value = 'Submitting your order';
            try {
                await transactionsStore.createOrder();
                if (kycStatus.value !== KycTypes.KycStatus.APPROVED) {
                    paymentCurrentStep.value = PaymentTypes.PaymentSteps.PAYMENT_1;
                    await router.push('/kyc');
                }
                else {
                    paymentCurrentStep.value = PaymentTypes.PaymentSteps.PAYMENT_2;
                }
            }
            catch (e) {
                error.value = 'Failed to create order';
            }
            loading.value = false;
            loadingMessage.value = '';
        };
        const goBack = () => {
            router.push('/transact');
            currentStep.value = TransactTypes.TransactSteps.BUY_3;
        };
        return {
            selectedCrypto,
            selectedCryptoAmount,
            numberFormat,
            onInputWalletEntered,
            onInputTagEntered,
            walletAddress,
            getBankDetails,
            buttonDisabled,
            BuyTypes: TransactTypes,
            KycTypes,
            kycStatus,
            goBack,
            error,
            loading,
            isLoggedIn,
            showHome,
            network,
            interceptorLoading,
            interceptorError,
            walletTagRequired,
            tagText,
            rules,
            travelRuleData,
            shouldTriggerTravelRule,
            onNameInputEntered,
            isCountryBotswana,
        };
    },
});
